import React from 'react';
import { Typography, Button, Row, Col, Card, Collapse, theme } from 'antd';
import { SafetyCertificateOutlined, ClockCircleOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LandingLayout from '../../components/Layouts/LandingLayout';
import AppLayout from '../../components/Layouts/Layout';
import { useAuth } from '../../contextProviders/AuthProvider';
import HomeSearchBar from '../../components/HomeSearchBar';
import HeroBanner, { HeroBannerImage } from '../../components/HeroBanner';
import DiscoverySpaces from '../../components/DiscoverySpaces';
import homepageConfig from '../../config/homepage.config.json';
import { IHomepageConfig } from '../../interfaces/IHomepageConfig';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { CORAL, TEAL } from '../../theme/creamyTokens';
import heroWebp from '../../assets/hero/worksite-night.webp';
import heroWebpMobile from '../../assets/hero/worksite-night-mobile.webp';
import heroJpg from '../../assets/hero/worksite-night.jpg';

const { Title, Paragraph } = Typography;

const config = homepageConfig as IHomepageConfig;

const FEATURE_ICONS: Record<string, React.ReactNode> = {
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  CreditCardOutlined: <CreditCardOutlined />,
};

const SECTION_WRAPPER: React.CSSProperties = { maxWidth: 1200, margin: '0 auto' };

// Decorative backdrop for the hero band -- no alt text on purpose, the
// headline laid over it already says what this page is (see HeroBannerImage).
const HERO_IMAGE: HeroBannerImage = { webp: heroWebp, webpMobile: heroWebpMobile, fallback: heroJpg };

// Hero copy sits on a photo, not on cream, so it can't inherit antd's own
// text colours. The shadow is what keeps it legible over the brighter parts
// of the picture (the floodlights, the hi-vis vests) where the scrim alone
// isn't enough -- soft and wide rather than a hard drop, so it reads as the
// letters lifting off the photo instead of as an outline.
const HERO_TITLE_STYLE: React.CSSProperties = {
  color: '#FFFFFF',
  fontWeight: 800,
  lineHeight: 1.12,
  letterSpacing: -0.6,
  marginTop: 0,
  textShadow: '0 2px 24px rgba(0,0,0,0.55)',
};

const HERO_SUBTITLE_STYLE: React.CSSProperties = {
  color: 'rgba(255,255,255,0.88)',
  textShadow: '0 1px 14px rgba(0,0,0,0.5)',
};

// Shared look for every icon/step card on the page (Features bar + How it
// works) -- rounded, shadowed, on a slightly elevated surface instead of a
// thin border with no fill, so they read as a matched set.
const INFO_CARD_STYLE: React.CSSProperties = {
  height: '100%',
  textAlign: 'center',
  borderRadius: 20,
  border: 'none',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
};

// Actual page body, split out from Landing so it can sit *inside* the
// coral ConfigProvider below -- theme.useToken() only picks up a nested
// provider's tokens when called by a component mounted under it, not by an
// ancestor that renders the provider itself (same reason HeavyDutyMachineryForm
// and HostProfileEditPage are split the same way).
const LandingContent: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const content = (
    <div style={{ width: '100%' }}>
      {/* 1. Hero -- the same headline and search control as before, now laid
          over a full-bleed worksite photo (see HeroBanner).

          Mobile keeps the original decision that the search control comes
          first, right under the header, rather than being pushed down by a
          marketing block: the headline moved BELOW the pill instead of
          above it. So the copy sits over the photo on both breakpoints
          without the pill losing the spot it was deliberately given. */}
      <HeroBanner
        image={HERO_IMAGE}
        height={isMobile ? 300 : 520}
        // The crew is spread across the photo's bottom third, so centring a
        // band shorter than the source would cut them in half; biased down
        // to keep them in frame while the machines still read above.
        focalPoint="center 58%"
        contentPadding={isMobile ? '18px 16px 56px' : '56px 24px 80px'}
      >
        {/* textAlign is set explicitly on both branches rather than left to
            inherit: LandingLayout/AppLayout's Content centres the whole
            page, and on a narrow screen centred copy under a left-aligned
            search pill read as two unrelated blocks. Desktop stays centred,
            exactly as it was. */}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, textAlign: 'left' }}>
            {/* The collapsed control is transparent-on-white by default
                (see HomeSearchBar's isMobile branch) -- over a night photo
                that leaves dark text on a dark background, so it gets a
                real filled surface here. Nothing else about it changes. */}
            <HomeSearchBar
              pulseOnMount
              style={{
                background: token.colorBgContainer,
                border: 'none',
                boxShadow: '0 10px 28px rgba(0,0,0,0.32)',
              }}
            />
            <div>
              <Title level={1} style={{ ...HERO_TITLE_STYLE, fontSize: 27, marginBottom: 8 }}>
                {translate(t, config.hero.titleKey)}
              </Title>
              <Paragraph style={{ ...HERO_SUBTITLE_STYLE, fontSize: 14.5, marginBottom: 0 }}>
                {translate(t, config.hero.subtitleKey)}
              </Paragraph>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
            <Title level={1} style={{ ...HERO_TITLE_STYLE, fontSize: 'clamp(34px, 4.2vw, 54px)', marginBottom: 14 }}>
              {translate(t, config.hero.titleKey)}
            </Title>
            <Paragraph style={{ ...HERO_SUBTITLE_STYLE, fontSize: 19, maxWidth: 640, margin: '0 auto 36px' }}>
              {translate(t, config.hero.subtitleKey)}
            </Paragraph>
            {/* A deeper shadow than the bar's own default -- on cream the
                default lifts it off the page, on a photo it needs to look
                like it's floating well above it. */}
            <HomeSearchBar style={{ marginBottom: 0, boxShadow: '0 20px 48px rgba(0,0,0,0.38)' }} />
          </div>
        )}
      </HeroBanner>

      {/* 2. Discovery: featured / top-rated / near you.
          Top padding is new: the hero's own bottom padding now sits INSIDE
          the photo band, so without it the first carousel's title would
          start on the pixel the banner ends (HeavyDutyMachineryCarousel has
          no top margin of its own, only a bottom one). */}
      <section style={{ padding: isMobile ? '28px 7px 24px' : '40px 24px 32px' }}>
        <div style={SECTION_WRAPPER}>
          <DiscoverySpaces />
        </div>
      </section>

      {/* 3. Features bar -- on isMobile these stack one per row (xs={24}), so
          the horizontal half of the gutter would otherwise still inset each
          card by antd's own Col padding even though there's nothing beside
          it to space out from; dropped to 0 there so the card actually
          reaches the same aggressive 7px side margin as the rest of the
          screen instead of an extra ~12px on top of it. */}
      <section style={{ padding: isMobile ? '32px 7px' : '32px 24px' }}>
        <div style={SECTION_WRAPPER}>
          <Row gutter={isMobile ? [0, 24] : [24, 24]} justify="center">
            {config.features.map((feature) => (
              <Col xs={24} sm={12} md={8} key={feature.titleKey}>
                <Card
                  hoverable
                  style={{ ...INFO_CARD_STYLE, borderRadius: isMobile ? 16 : INFO_CARD_STYLE.borderRadius, background: token.colorFillAlter }}
                  styles={{ body: { padding: 32 } }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: token.colorFillTertiary,
                      color: token.colorPrimary,
                      fontSize: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    {FEATURE_ICONS[feature.icon]}
                  </div>
                  <Title level={4} style={{ fontWeight: 600, marginBottom: 8 }}>
                    {translate(t, feature.titleKey)}
                  </Title>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {translate(t, feature.descriptionKey)}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 4. How it works -- same isMobile full-bleed/gutter treatment as the
          Features bar above. Desktop keeps the coral/teal alternation (an
          earlier, deliberate choice for that breakpoint); on mobile every
          step uses the same coral as the rest of the redesigned mobile
          screens (buttons, icons, brand mark) instead of introducing an
          unrelated green into an otherwise coral-only palette. */}
      <section style={{ padding: isMobile ? '64px 7px' : '64px 24px' }}>
        <div style={SECTION_WRAPPER}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 40 }}>
            {translate(t, config.howItWorks.titleKey)}
          </Title>
          <Row gutter={isMobile ? [0, 24] : [24, 24]} justify="center">
            {config.howItWorks.steps.map((step, index) => (
              <Col xs={24} sm={12} md={8} key={step.titleKey}>
                <Card
                  style={{ ...INFO_CARD_STYLE, borderRadius: isMobile ? 16 : INFO_CARD_STYLE.borderRadius, background: token.colorFillAlter }}
                  styles={{ body: { padding: 32 } }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: isMobile ? CORAL : index % 2 === 0 ? CORAL : TEAL,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      fontWeight: 700,
                    }}
                  >
                    {index + 1}
                  </div>
                  <Title level={4} style={{ fontWeight: 600, marginBottom: 8 }}>
                    {translate(t, step.titleKey)}
                  </Title>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {translate(t, step.descriptionKey)}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 5. FAQ -- on mobile, each question gets its own separated, fully
          rounded panel (ghost + explicit spacing) instead of antd's default
          single square-cornered bordered block, matching the "smooth,
          creamy pill" card language used everywhere else on this page. */}
      <section style={{ padding: '64px 24px' }}>
        <div style={{ ...SECTION_WRAPPER, maxWidth: 800 }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 40 }}>
            {translate(t, config.faq.titleKey)}
          </Title>
          <Collapse
            ghost={isMobile}
            style={isMobile ? { display: 'flex', flexDirection: 'column', gap: 12 } : undefined}
            items={config.faq.items.map((item, index) => ({
              key: index,
              label: translate(t, item.questionKey),
              children: <Paragraph style={{ marginBottom: 0 }}>{translate(t, item.answerKey)}</Paragraph>,
              style: isMobile
                ? { background: token.colorFillAlter, borderRadius: 16, border: 'none', overflow: 'hidden' }
                : undefined,
            }))}
          />
        </div>
      </section>

      {/* 6. Final CTA */}
      <section style={{ textAlign: 'center', padding: '64px 24px', background: token.colorFillAlter }}>
        <Title level={2}>{translate(t, config.finalCta.titleKey)}</Title>
        <Paragraph style={{ fontSize: 16, maxWidth: 560, margin: '0 auto 24px' }}>
          {translate(t, config.finalCta.subtitleKey)}
        </Paragraph>
        <Button
          type="primary"
          size="large"
          style={{ borderRadius: 999 }}
          onClick={() => navigate(config.finalCta.button.link)}
        >
          {translate(t, config.finalCta.button.labelKey)}
        </Button>
      </section>
    </div>
  );

  return content;
};

const Landing: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isMobile } = useAppBreakpoint();

  // Split into a wrapper + Content component so that a scoped theme can
  // be added here without moving anything: theme.useToken() only sees a
  // nested ConfigProvider's tokens from a component mounted UNDER it, not
  // from the one rendering the provider. There is nothing scoped here
  // today -- coral is the app-wide primary (see theme/appTheme).
  const content = isMobile ? (
    <LandingContent isMobile={isMobile} />
  ) : (
    <LandingContent isMobile={isMobile} />
  );

  // Same homepage either way -- signed-in visitors get the normal app
  // chrome (AppHeader/AppFooter), signed-out ones get LandingLayout's
  // Login/Register header instead of AppLayout blanking the page (its own
  // `if (!isAuthenticated) return null`).
  if (authLoading) return null;
  if (isAuthenticated) return <AppLayout>{content}</AppLayout>;
  return <LandingLayout>{content}</LandingLayout>;
};

export default Landing;
